import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import MasterAdminDashboardList from "./MasterAdminDashboardList";
import { SchoolListType } from "utils/interfaces";
import {
  MainContext,
  MasterAdminDashboardContext,
} from "components/utils/Contexts";
import { MdCorporateFare } from "react-icons/md";
import { AnimatePresence } from "framer-motion";
import {
  deleteRecordRequest,
  getCollectionRequest,
} from "services/apiRequests";
import AddNewSchool from "../../utils/create-edit-school";
import ViewHeader from "components/main-view/utils/view-header";
import CustomerAdminDashboard from "../customer-admin";
import SavedSuccessfulPanel from "components/main-view/utils/pop-up-panels/saved-successful-pop-up";
import ConfirmDeletionPanel from "components/main-view/utils/pop-up-panels/confirm-deletion-pop-up";
import { usePlatform } from "components/utils/PlatformContext";
import { singlify } from "utils/utils";
import { logoutFunction } from "services/authRequests";

interface MasterAdminDashboardProps {
  ref: any;
  headerRef: React.MutableRefObject<any>;
  subHeaderRef: React.MutableRefObject<any>;
  customerAdminDashboardRef?: React.MutableRefObject<any>;
}

const MasterAdminDashboard: React.FC<MasterAdminDashboardProps> = forwardRef(
  ({ headerRef, subHeaderRef, customerAdminDashboardRef }, ref) => {
    // States definition
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedData, setSelectedData] = useState<SchoolListType | null>(
      null,
    );
    const [showSavedSuccessfully, setShowSavedSuccessfully] = useState(false);
    const [showConfirmDeletePanel, setShowConfirmDeletePanel] = useState(false);
    const [display, setDisplay] = useState<
      "main" | "create-edit" | "inside-school"
    >("main");
    // State used to manage the image cache
    const [version, setVersion] = useState(0);
    // States used for the list control
    const [data, setData] = useState<SchoolListType[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [schoolToDelete, setSchoolToDelete] = useState<SchoolListType | null>(
      null,
    );

    // Get context parameters
    const { classesTerminology, studentsTerminology, schoolTerminology } =
      usePlatform();
    const { userRole, setContextSchool, setShowSideBarMenu } =
      useContext(MainContext);

    // Add customer reference definition
    const addCustomerRef: React.Ref<any> = useRef(null);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      getData() {
        getData();
      },
      setSelectedData(data: SchoolListType | null) {
        setSelectedData(data);
      },
      setCreateSchool(bool: boolean) {
        setDisplay(bool ? "create-edit" : "main");
      },
      pressSaveButton() {
        let currentHandler: any;

        // If we are working with schools
        if (["main", "create-edit"].includes(display)) {
          if (addCustomerRef.current) currentHandler = addCustomerRef.current;
          currentHandler.handleSaveSchool();
        }
        // In the case of creating a new student
        else {
          if (customerAdminDashboardRef && customerAdminDashboardRef.current)
            currentHandler = customerAdminDashboardRef.current;
          currentHandler.handleSaveNewStudent();

          // And comeback to the previous view
          getBackToSelectedClass();
        }
      },
      onBackButtonClick() {
        onBackButtonClick();
      },
      onCreateEditSchool(title: string, description: string) {
        onCreateEditSchool("create", title, description);
      },
      onDiscard() {
        onDiscard();
      },
      handleSave() {
        handleSave();
      },
    }));

    /**
     * Root definition
     */
    useEffect(() => {
      onBackButtonClick();
      getData();
    }, []);

    /**
     * Each time selected data is changed
     */
    useEffect(() => {
      getData();
    }, [selectedData]);

    /**
     * Function used to define the create edit school
     * @param title
     * @param description
     */
    const onCreateEditSchool = (
      type: "create" | "edit",
      title = `New ${singlify(schoolTerminology)}`,
      description = singlify(schoolTerminology),
    ) => {
      // Set the status for create edit school
      setDisplay("create-edit");
      // And hide the pop up message
      setShowSavedSuccessfully(false);

      // (Main Header) Set titles and the back button
      setHeaderTitleAndDescription(title, description);

      // Force delete the context if creating
      if (type === "create") setContextSchool(null);
    };

    /**
     * Function applied when we click on the card
     */
    const handleCardClick = (card: SchoolListType) => {
      return (event: React.MouseEvent) => {
        // Prevent activate the click on the whole card
        event.stopPropagation();

        // Select the current data
        setSelectedData(card);
        // Add to the context
        setContextSchool(card);
        // Set the customers panels
        setDisplay("inside-school");

        // Show the back button
        headerRef.current.setHeaderBackButton(true);
        // Define the type of the show list
        headerRef.current.options().setCreateTypeButton("classes");
        // Set the title and description
        headerRef.current.setHeaderTitle(card.name);
        headerRef.current.setHeaderDescription(
          <div className="w-fit flex gap-1">
            <MdCorporateFare />
            <div>{singlify(schoolTerminology)} Panel</div>
          </div>,
        );
      };
    };

    /**
     * Function applied when we press the edit button on cards
     * @param card
     */
    const clickEdit = (card: SchoolListType) => {
      // Select the current data
      setSelectedData(card);

      // Set the current school as the context one if the user is a master admin
      if (userRole === "master admin") setContextSchool(card);

      // Show the back button
      headerRef.current.setHeaderBackButton(true);

      // Set the same view as when creating a school
      onCreateEditSchool(
        "edit",
        card.name,
        `${singlify(schoolTerminology)} Profile`,
      );
    };

    /**
     * Wrapper to transfor the clickEdit function into a handler
     */
    const handleClickEdit = (card: SchoolListType) => {
      return (event: React.MouseEvent) => {
        // Prevent activate the click on the whole card
        event.stopPropagation();

        // And click edit
        clickEdit(card);
      };
    };

    /**
     * Delete click definition
     */
    const handleClickDelete = (card: SchoolListType) => {
      return (event: React.MouseEvent) => {
        // Prevent activate the click on the whole card
        event.stopPropagation();

        // Open the confirm delete panel
        setShowConfirmDeletePanel(true);

        // And set the lesson to delete
        setSchoolToDelete(card);
      };
    };

    /**
     * Function executed when closing the confirm deletion pop up
     */
    const handleCloseConfirmDelete = () => {
      // Close the pop up
      setShowConfirmDeletePanel(false);

      // And reset the lesson to delete
      setSchoolToDelete(null);
    };

    /**
     * Function executed when confirming the deletion of the lesson
     */
    const handleClickConfirmDelete = async () => {
      // Check if the record and the lessonType is defined
      if (!schoolToDelete) return;

      // Execute the request
      const _id = schoolToDelete._id;
      const response = await deleteRecordRequest({ _id }, "/api/organisations");

      // Check if everything went right with the request
      if (!(response && response.successful)) return;

      // Force and update to the list
      getData();

      // And reset the lesson to delete
      setSchoolToDelete(null);
    };

    /**
     * Function to get the school data
     * @returns data
     */
    const getData = async (filter = {}) => {
      // Set loading animation
      setIsLoadingData(true);

      // Define the population option
      const response = await getCollectionRequest("/api/organisations");

      // Check if the response is successful
      if (!response.successful) {
        // Check if it's not successful because of a token expiration
        if (response.logout) {
          // alert(response.message);
          return await logoutFunction();
        }

        return [];
      }

      // Get the data
      const data = response.content;

      // Assign the data to the corresponding element
      setData(data);

      // Stop loading animation
      setIsLoadingData(false);

      return data;
    };

    /**
     * Function used to set title and description
     * @param title
     * @param description
     */
    const setHeaderTitleAndDescription = (
      title: string,
      description: string,
      addIcon: boolean = true,
    ) => {
      // Set the title and description
      headerRef.current.setHeaderTitle(title);
      headerRef.current.setHeaderDescription(
        addIcon ? (
          <div className="w-fit flex gap-1">
            <MdCorporateFare />
            <div>{description}</div>
          </div>
        ) : (
          description
        ),
      );
    };

    /**
     * Function executed when clicking back inside "Create new Student" to avoid
     * coming back to the list of Schools.
     */
    const getBackToSelectedClass = () => {
      // First of all, check if there is selected data
      if (!selectedData) return;

      // Activate the side bar menu
      setShowSideBarMenu(true);

      // Also check that the customerAdminDashboardRef exists
      if (!(customerAdminDashboardRef && customerAdminDashboardRef.current))
        return;

      // Set the state to false
      customerAdminDashboardRef.current.setCreateNewStudent(false);

      // Activating the create new button
      headerRef.current.options().setShowCreateNewButton(true);
      // Activate the sub header
      subHeaderRef.current.setShow(true);
      // And turn off the other buttons
      headerRef.current
        .options()
        .setSaveButton({ show: false, disabled: true });

      // And set the title and description again
      setHeaderTitleAndDescription(
        selectedData.name,
        `${singlify(schoolTerminology)} Panel`,
      );
    };

    /**
     * Function used when clicking back button on the header
     * @returns
     */
    const onBackButtonClick = () => {
      // Check if we have the customer admin view (after clicked a card)
      if (selectedData) {
        // Check if we are inside the create new student option
        if (customerAdminDashboardRef && customerAdminDashboardRef.current) {
          // Get the internal state
          const createNewStudent =
            customerAdminDashboardRef.current.getCreateNewStudent();

          // If we are inside the create new student
          if (createNewStudent) {
            // Come back to the previous view
            getBackToSelectedClass();
            return;
          }
        }
      }

      // Once pressed, come back to the root view
      onDiscardCreateEditSchool();

      // And force unselect data
      setSelectedData(null);
    };

    /**
     * Function that defines the root view and coordinates
     * the different discard actions
     */
    const onDiscard = () => {
      // Check the state of the component
      if (["main", "create-edit"].includes(display))
        onDiscardCreateEditSchool();
      else if (display === "inside-school") onBackButtonClick();
    };

    /**
     * Function that defines discard behaviour when
     * creating/editing schools
     */
    const onDiscardCreateEditSchool = () => {
      // Activate the side bar menu
      setShowSideBarMenu(true);
      // And force unselect the context school (used for master admin)
      setContextSchool(null);

      // And set the status
      setDisplay("main");
      // And force unselect data
      setSelectedData(null);

      // (Main Header) Change the titles and back button
      setHeaderTitleAndDescription(
        schoolTerminology,
        `List of current ${schoolTerminology.toLowerCase()}`,
        false,
      );
      headerRef.current.setShowBackButton(false);

      // (Dashboard Header) Change the button settings
      // Delete the generate report button
      headerRef.current.options().setShowGenerateReportsButton(false);
      // Activating the create new button
      headerRef.current.options().setShowCreateNewButton(true);
      // And turn off the other buttons
      headerRef.current
        .options()
        .setSaveButton({ show: false, disabled: true });
      headerRef.current.options().setCreateTypeButton("customers");

      // And force hide the subheader
      subHeaderRef.current.setShow(false);
    };

    /**
     * Function that coordinates the save function based on
     * the internal state of the component
     */
    const handleSave = () => {
      // Check the state of the component
      if (display === "create-edit" && addCustomerRef.current)
        addCustomerRef.current.handleSaveSchool();
      else if (
        display === "inside-school" &&
        customerAdminDashboardRef &&
        customerAdminDashboardRef.current
      )
        customerAdminDashboardRef.current.handleSaveNewStudent();
    };

    /**
     * Function used after save a school
     */
    const afterSaveFunction = async () => {
      // Everytime this function is called, update the version
      setVersion((currentVersion) => currentVersion + 1);

      // Display the successful message
      setShowSavedSuccessfully(true);

      // Discard the create edit view
      onDiscardCreateEditSchool();

      // And refresh the data
      await getData();
    };

    return (
      <MasterAdminDashboardContext.Provider value={{ version, setVersion }}>
        <div className="w-full self-stretch flex-col justify-start items-start gap-4 inline-flex">
          {display === "main" ? (
            <>
              <ViewHeader
                searchQuery={searchQuery}
                activeItem={""}
                tabOptions={[]}
                showTabOptions={[
                  "master admin",
                  "customer admin",
                  "class admin",
                ].includes(userRole)}
                setActiveItem={() => {}}
                setSearchQuery={setSearchQuery}
                headerRef={headerRef}
              />
              <MasterAdminDashboardList
                data={data}
                searchQuery={searchQuery}
                isLoadingData={isLoadingData}
                handleCardClick={handleCardClick}
                handleClickEdit={handleClickEdit}
                handleClickDelete={handleClickDelete}
                onCreateEditSchool={onCreateEditSchool}
              />
            </>
          ) : display === "create-edit" ? (
            <AddNewSchool
              ref={addCustomerRef}
              afterSaveFunction={afterSaveFunction}
              selectedData={selectedData}
              headerRef={headerRef}
            />
          ) : (
            <CustomerAdminDashboard
              ref={customerAdminDashboardRef}
              userSchool={selectedData}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
            />
          )}
          {/* Pop up panels */}
          <AnimatePresence>
            {/* Saved successful message pop-up */}
            {showSavedSuccessfully && (
              <SavedSuccessfulPanel
                title={`${singlify(schoolTerminology)} Added Successfully`}
                paragraphs={[
                  `The new ${singlify(schoolTerminology).toLowerCase()} has been successfully added to the system.`,
                  `Feel free to dive in to start customizing the ${singlify(schoolTerminology).toLowerCase()}'s profile, setting up ${classesTerminology.toLowerCase()}, and inviting staff, ${studentsTerminology.toLowerCase()}, and parents to join the platform.`,
                ]}
                createText="Add another"
                onCreateAnother={() => onCreateEditSchool("create")}
                onClose={() => setShowSavedSuccessfully(false)}
              />
            )}
            {/* Confirm delete lesson */}
            {showConfirmDeletePanel && (
              <ConfirmDeletionPanel
                title="Confirm Deletion"
                paragraphs={[
                  "This action cannot be undone.",
                  "All related data will be permanently removed from the system.",
                ]}
                deleteText="Delete"
                onDelete={handleClickConfirmDelete}
                onClose={handleCloseConfirmDelete}
              />
            )}
          </AnimatePresence>
        </div>
      </MasterAdminDashboardContext.Provider>
    );
  },
);

export default MasterAdminDashboard;
